import type { OrgLike } from "./orgPlugins";
import { type PluginHookContribution, definePluginHook } from "./pluginHook";

/** Resolves an insert index from the columns accumulated so far. */
export type ColumnPlacement = (
  columns: readonly { id: string }[]
) => number | null | undefined;

type ColumnsContribution<Column> = {
  columns: readonly Column[];
  place?: ColumnPlacement;
};

export type PluginColumnsHook<Column extends { id: string }> = {
  readonly id: string;
  contribute(
    fn: () => readonly Column[] | void,
    place?: ColumnPlacement
  ): PluginHookContribution;
  compose(organization: OrgLike, hostColumns: readonly Column[]): Column[];
};

function assertUniqueColumnIds<Column extends { id: string }>(
  hookId: string,
  columns: readonly Column[]
): Column[] {
  const seen = new Set<string>();

  for (const column of columns) {
    if (seen.has(column.id)) {
      throw new Error(
        `Duplicate column "${column.id}" in plugin hook "${hookId}".`
      );
    }

    seen.add(column.id);
  }

  return [...columns];
}

function insertColumns<Column extends { id: string }>(
  columns: readonly Column[],
  contribution: readonly Column[],
  place?: ColumnPlacement
): Column[] {
  const index = place?.(columns);
  const insertIndex =
    index === null || index === undefined || index < 0 || index > columns.length
      ? columns.length
      : index;
  const next = [...columns];
  next.splice(insertIndex, 0, ...contribution);
  return next;
}

export function placeAfterColumn(columnId: string): ColumnPlacement {
  return (columns) => {
    const index = columns.findIndex((column) => column.id === columnId);
    return index === -1 ? undefined : index + 1;
  };
}

export function placeBeforeColumn(columnId: string): ColumnPlacement {
  return (columns) => {
    const index = columns.findIndex((column) => column.id === columnId);
    return index === -1 ? undefined : index;
  };
}

/**
 * Defines a host-owned extension point for ordered columns.
 *
 * Contributions append by default. A placement callback can insert a
 * contribution relative to columns already composed by the host and earlier
 * plugins.
 */
export function definePluginColumnsHook<Column extends { id: string }>(config: {
  name: string;
}): PluginColumnsHook<Column> {
  const contributionsHook = definePluginHook<
    void,
    readonly ColumnsContribution<Column>[]
  >({
    name: config.name,
    initial: () => [],
    merge: (contributions, next) => [...contributions, ...next],
  });

  return {
    id: config.name,
    contribute(fn, place) {
      return contributionsHook.contribute(() => {
        const columns = fn();
        if (columns === undefined) {
          return undefined;
        }

        return [{ columns, place }];
      });
    },
    compose(organization, hostColumns) {
      const contributions = contributionsHook.run(organization, undefined);
      const columns = contributions.reduce(
        (accumulated, contribution) =>
          insertColumns(accumulated, contribution.columns, contribution.place),
        [...hostColumns]
      );

      return assertUniqueColumnIds(config.name, columns);
    },
  };
}
